import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../auth/AuthProvider';
import { useTheme } from '../../hooks/useTheme';
import { useAppDeployPolling } from '../../hooks/useAppDeployPolling';
import { useDeployEnvironment, isSimulationMode } from '../../hooks/useDeployEnvironment';
import { useDeployTracker } from '../../contexts/DeployTrackerContext';
import { postDeploy } from '../../lib/deploy-api';
import { postDeployFromGit } from '../../lib/github-api';
import { COLORS, FONTS } from '../../styles/tokens';
import { DeployStepper, type DeployStepId } from '../../components/app/DeployStepper';
import { EnvironmentBanner } from '../../components/app/EnvironmentBanner';
import { DeploySuccessCard } from '../../components/app/DeploySuccessCard';
import { DetailsCard, UploadCard, ReviewCard } from '../../components/app/DeployFormCards';
import { SecretCaptureStep } from '../../components/app/SecretCaptureStep';
import { SourceTypeTabs, type SourceType } from '../../components/app/SourceTypeTabs';
import { type GitHubSelection } from '../../components/app/GitHubConnector';
import { GitHubSourceCard, GitReviewCard } from '../../components/app/GitHubSourceCard';
import { ConfirmationModal } from '../../components/modal/ConfirmationModal';
import type { ChatMessage } from '../../components/chat/types';

const MAX_UPLOAD_BYTES = 100 * 1024 * 1024;

/**
 * Form-first deploy page. Replaces the chat-only flow with a plain stepper:
 * Details → Upload → Review → Deploy. Build progress streams into a drawer.
 * An AI help link at the footer opens the old chat surface for troubleshooting.
 */
export default function AppDeployForm(): React.JSX.Element {
  const { token } = useAuth();
  const { isDark } = useTheme();
  const colors = isDark ? COLORS.dark : COLORS.light;
  const navigate = useNavigate();

  const [step, setStep] = useState<DeployStepId>('name');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [team, setTeam] = useState('');
  const [category, setCategory] = useState('User Apps');
  const [icon, setIcon] = useState<File | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [sourceType, setSourceType] = useState<SourceType>('zip');
  const [githubSelection, setGithubSelection] = useState<GitHubSelection | null>(null);
  const [capturedSecrets, setCapturedSecrets] = useState<Array<{ key: string; value: string; isSecret: boolean }>>([]);
  const [showDeployModal, setShowDeployModal] = useState(false);
  const [fileTooLarge, setFileTooLarge] = useState(false);
  const [deployError, setDeployError] = useState<string | null>(null);
  const [deployed, setDeployed] = useState<{ slug: string; appUrl: string; version: number } | null>(null);
  const [pollMessages, setPollMessages] = useState<ChatMessage[]>([]);

  const deployEnv = useDeployEnvironment();
  const simulated = isSimulationMode(deployEnv);
  const tracker = useDeployTracker();
  const pollBuildStatus = useAppDeployPolling({
    token,
    append: msg => setPollMessages(prev => [...prev, msg]),
    simulated,
  });
  const activeTrack = deployed ? tracker.tracks.find(t => t.slug === deployed.slug) : undefined;

  const acceptFile = useCallback((picked: File) => {
    setDeployError(null);
    if (picked.size > MAX_UPLOAD_BYTES) { setFileTooLarge(true); setFile(null); return; }
    setFileTooLarge(false);
    setFile(picked);
    setCapturedSecrets([]);
    setStep('secrets');
  }, []);

  const submitName = useCallback(() => {
    if (name.trim().length === 0) return;
    setStep('upload');
  }, [name]);

  const submitDeploy = useCallback(async () => {
    if (name.trim().length === 0) return;
    if (sourceType === 'zip' && !file) return;
    if (sourceType === 'github' && !githubSelection) return;
    setStep('building');
    setDeployError(null);
    try {
      const result = sourceType === 'zip'
        ? await postDeploy(token, { zip: file as File, name: name.trim(), description, team, category, icon, secrets: capturedSecrets })
        : await postDeployFromGit(token, {
            name: name.trim(), description, team, category,
            installationId: (githubSelection as GitHubSelection).installationId,
            owner: (githubSelection as GitHubSelection).owner,
            repo: (githubSelection as GitHubSelection).repo,
            branch: (githubSelection as GitHubSelection).branch,
          });
      if (!result.ok || !result.data.slug || !result.data.appUrl) {
        setDeployError(result.data.error ?? 'Deploy request failed');
        setStep('review');
        return;
      }
      const slug = result.data.slug;
      const appUrl = result.data.appUrl;
      setDeployed({ slug, appUrl, version: 1 });
      tracker.startTrack({ slug, name: name.trim(), appUrl, version: 1 });
      void pollBuildStatus(slug);
    } catch (err) {
      setDeployError(err instanceof Error ? err.message : 'Deploy request failed');
      setStep('review');
    }
  }, [file, sourceType, githubSelection, name, token, pollBuildStatus, description, team, category, icon, capturedSecrets, tracker]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', maxWidth: '720px', margin: '0 auto', padding: '8px 0' }}>
      <Header colors={colors} onBack={() => navigate('/apps')} />
      {deployEnv && <EnvironmentBanner env={deployEnv} colors={colors} />}
      <DeployStepper
        currentStep={step}
        textPrimary={colors.text}
        textSecondary={colors.textSecondary}
        accent="#01A9ED"
        border={colors.border}
      />

      {step === 'name' && (
        <DetailsCard
          colors={colors} isDark={isDark}
          name={name} setName={setName}
          description={description} setDescription={setDescription}
          team={team} setTeam={setTeam}
          category={category} setCategory={setCategory}
          icon={icon} setIcon={setIcon}
          onNext={submitName}
        />
      )}

      {step === 'upload' && (
        <div>
          <SourceTypeTabs
            value={sourceType}
            onChange={setSourceType}
            textPrimary={colors.text}
            textSecondary={colors.textSecondary}
            border={colors.border}
            accent="#01A9ED"
          />
          {sourceType === 'zip' ? (
            <UploadCard
              colors={colors} isDark={isDark}
              fileTooLarge={fileTooLarge}
              maxBytes={MAX_UPLOAD_BYTES}
              onFileSelected={acceptFile}
              onBack={() => setStep('name')}
            />
          ) : (
            <GitHubSourceCard
              colors={colors}
              token={token}
              selection={githubSelection}
              onSelect={setGithubSelection}
              onBack={() => setStep('name')}
              onContinue={() => setStep('review')}
            />
          )}
        </div>
      )}

      {step === 'secrets' && file && (
        <SecretCaptureStep
          token={token}
          zip={file}
          colors={colors}
          onBack={() => setStep('upload')}
          onContinue={secrets => { setCapturedSecrets(secrets); setStep('review'); }}
        />
      )}

      {(step === 'review' || step === 'building') && sourceType === 'zip' && file && (
        <ReviewCard
          colors={colors} isDark={isDark}
          name={name} description={description} team={team} category={category}
          file={file}
          building={step === 'building'}
          error={deployError}
          onDeploy={() => setShowDeployModal(true)}
          onChangeFile={() => { setFile(null); setStep('upload'); }}
          onEditDetails={() => setStep('name')}
        />
      )}

      {(step === 'review' || step === 'building') && sourceType === 'github' && githubSelection && (
        <GitReviewCard
          colors={colors} isDark={isDark}
          name={name} description={description} team={team} category={category}
          selection={githubSelection}
          building={step === 'building'}
          error={deployError}
          onDeploy={() => setShowDeployModal(true)}
          onChangeSource={() => { setGithubSelection(null); setStep('upload'); }}
          onEditDetails={() => setStep('name')}
        />
      )}

      <ConfirmationModal
        open={showDeployModal}
        variant="primary"
        title={`Deploy ${name || 'app'}?`}
        body={
          <>
            This will validate the zip, generate a Dockerfile (if needed), build
            the image, apply k8s resources, register a Keycloak client + theme,
            and wait for the pod to become ready.
          </>
        }
        confirmLabel="Deploy now"
        onCancel={() => setShowDeployModal(false)}
        onConfirm={async () => {
          setShowDeployModal(false);
          await submitDeploy();
        }}
      />

      {deployed && activeTrack?.currentStage === 'done' && (
        <DeploySuccessCard
          name={name.trim() || deployed.slug}
          appUrl={deployed.appUrl}
          version={deployed.version}
          isDark={isDark}
          colors={colors}
        />
      )}
      {deployed && activeTrack?.currentStage === 'failed' && pollMessages.length > 0 && (
        <PollMessagesBox messages={pollMessages} colors={colors} />
      )}

      <HelpFooter onOpen={() => navigate('/apps/deploy-chat')} colors={colors} />
    </div>
  );
}

/** Page title + "Back to Apps" button. */
function Header(props: { colors: { text: string; border: string; textSecondary: string }; onBack: () => void }): React.JSX.Element {
  return (
    <header className="vx-page-toolbar" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px', gap: '12px' }}>
      <h2 style={{ fontFamily: FONTS.heading, fontSize: '18px', letterSpacing: '0.15em', color: props.colors.text, textTransform: 'uppercase', margin: 0 }}>
        Deploy a New App
      </h2>
      <button
        onClick={props.onBack}
        style={{
          padding: '6px 14px', borderRadius: '6px',
          border: `1px solid ${props.colors.border}`, background: 'transparent',
          color: props.colors.textSecondary, fontFamily: FONTS.body, fontSize: '12px', cursor: 'pointer',
        }}
      >
        Back to Apps
      </button>
    </header>
  );
}

/** Stream of assistant messages emitted by the polling hook. */
function PollMessagesBox(props: { messages: ChatMessage[]; colors: { text: string; card: string; border: string } }): React.JSX.Element {
  return (
    <div style={{ marginTop: '20px', padding: '14px', borderRadius: '10px', border: `1px solid ${props.colors.border}`, backgroundColor: props.colors.card }}>
      {props.messages.map((msg, i) => (
        <div key={i} style={{ fontFamily: FONTS.body, fontSize: '13px', color: props.colors.text, padding: '6px 0', whiteSpace: 'pre-wrap' }}>
          {msg.content}
        </div>
      ))}
    </div>
  );
}

/** Footer link to the AI deploy assistant (secondary surface). */
function HelpFooter(props: { onOpen: () => void; colors: { textSecondary: string } }): React.JSX.Element {
  return (
    <div style={{ marginTop: '24px', textAlign: 'center' }}>
      <button
        onClick={props.onOpen}
        style={{
          border: 'none', background: 'transparent',
          color: props.colors.textSecondary, fontFamily: FONTS.body,
          fontSize: '12px', cursor: 'pointer', textDecoration: 'underline',
        }}
      >
        Need help? Ask the AI deploy assistant →
      </button>
    </div>
  );
}
